<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import type { ClassValue, HTMLInputAttributes } from "svelte/elements";
	import check from "@core/assets/icons/check.svg?raw";
	import circleCheckFillIcon from "@core/assets/icons/circle-check-fill.svg?raw";
	import errorIcon from "@core/assets/icons/error.svg?raw";
	import loaderIcon from "@core/assets/icons/loader.svg?raw";
	import { emailPattern } from "@core/utils/email.js";
	import { entries } from "@core/utils/entries.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { getTranslate } from "../../utils/getTranslate.js";
	import translations from "./Input.translations.json" with { type: "json" };
	import { InputValidationStyle } from "./InputValidationStyle.js";
	import { customValidationMessages } from "./customValidationMessages.js";

	let {
		value = $bindable(),
		group = $bindable(),
		checked = $bindable(),
		input = $bindable(),
		/* eslint-disable prefer-const */
		placeholder,
		wrapperClass,
		validationStyle = InputValidationStyle.Error,
		validationMessage,
		...rest
		/* eslint-enable prefer-const */
	}: Omit<HTMLInputAttributes, "value" | "group"> & {
		wrapperClass?: ClassValue;
		group?: unknown;
		value?: string;
		input?: HTMLInputElement;
		validationStyle?: InputValidationStyle;
		validationMessage?: string;
	} = $props();
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);
	const componentId = $props.id();

	const type = $derived(rest.type ?? "text");
	const customPattern: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, string>> = {
		email: emailPattern,
		phone: "\\+?[0-9\\s-]{7,}",
	};
	const pattern = $derived(rest.pattern ?? customPattern[type]);
	const isRadioOrCheckbox = $derived(["checkbox", "radio"].includes(String(rest.type)));

	let validityMessage = $state("");
	let showValidationPopup = $state(false);
	const displayMessage = $derived(
		validationMessage ?? (validationStyle === InputValidationStyle.Loading ? translate("loading") : validityMessage),
	);

	function validate(input: HTMLInputElement): void {
		const [, message] = entries(customValidationMessages).find(([key]) => input.validity[key]) ?? [];
		validityMessage = message?.(input)[tenant.locale] ?? "";
	}

	$effect(() => {
		if (!showValidationPopup) {
			return;
		}
		function hidePopup(): void {
			showValidationPopup = false;
		}
		// Registered after the current click has finished dispatching, so the very click that opened
		// the popup (e.g. the invalid form submit) doesn't immediately close it again.
		const timeout = window.setTimeout(() => {
			window.addEventListener("click", hidePopup);
		}, 0);
		return () => {
			window.clearTimeout(timeout);
			window.removeEventListener("click", hidePopup);
		};
	});

	const inputProps = $derived<HTMLInputAttributes>({
		...rest,
		// For acessibility, always provide with the real placeholder, and as it acts as a label as well, label the input by it.
		"aria-placeholder": placeholder,
		"aria-labelledby": isRadioOrCheckbox ? undefined : componentId,
		readonly: validationStyle === InputValidationStyle.Loading || rest.readonly,
		class: tw(
			"min-w-0 block shadow-sm transition-[outline,color] duration-300 !outline-none font-medium",
			isRadioOrCheckbox
				? [
						"appearance-none size-6 border checked:border-fcrdarkgray disabled:cursor-not-allowed",
						"cursor-pointer checked:border-fcrdarkgray checked:bg-fcrdarkgray",
						type === "radio" ? "rounded-full border-fcrgray" : "rounded-lg border-fcrlightgray",
					]
				: [
						"min-h-16 md:min-h-18 border border-fcrlightgray-300 rounded-3xl bg-fcrlightgray-200",
						"text-base text-fcrdarkgray placeholder:text-fcrgray px-6 peer focus:border-fcrgray focus:border-2",
						// If the value is not empty, change the padding so the label can be there.
						value && placeholder && "pt-7 pb-1",
						// If there is a label, and the input is focused, even when the value is empty,
						// change the padding so the label can be there
						"[&[aria-placeholder]:focus]:pt-7 [&[aria-placeholder]:focus]:pb-1",
						"transition-[padding] duration-200 w-full disabled:text-fcrgray",
						// Invalid/valid input styles
						validationStyle === InputValidationStyle.Warning && displayMessage
							? "border-fcrorange border-2"
							: validationStyle === InputValidationStyle.Success && displayMessage
								? "border-fcrsuccess border-2"
								: validationStyle !== InputValidationStyle.Loading &&
									displayMessage &&
									"[&:user-invalid]:border-fcrerror [&:user-invalid]:border-2",
						validationStyle === InputValidationStyle.Loading && "pointer-events-none",
					],
			type === "search" && "pr-[2rem]",
			rest.class,
		),
		pattern,
		oninput: async (event: Event & { currentTarget: EventTarget & HTMLInputElement }): Promise<void> => {
			const { currentTarget } = event;
			await rest.oninput?.(event);
			validate(currentTarget);
		},
		oninvalid: async (event: Event & { currentTarget: EventTarget & HTMLInputElement }): Promise<void> => {
			event.preventDefault();
			validate(event.currentTarget);
			if (isRadioOrCheckbox) {
				showValidationPopup = true;
			}
			await rest.oninvalid?.(event);
		},
		onchange: async (event: Event & { currentTarget: EventTarget & HTMLInputElement }): Promise<void> => {
			if (isRadioOrCheckbox) {
				showValidationPopup = false;
			}
			await rest.onchange?.(event);
		},
		onblur: async (event: FocusEvent & { currentTarget: EventTarget & HTMLInputElement }): Promise<void> => {
			if (isRadioOrCheckbox) {
				showValidationPopup = false;
			}
			await rest.onblur?.(event);
		},
	});
</script>

<div class={["relative", wrapperClass, validationStyle === InputValidationStyle.Loading && "cursor-wait"]}>
	{#if type === "checkbox" || type === "radio"}
		{#if type === "radio" || group}
			<input {...inputProps} bind:group {value} bind:this={input} />
		{:else}
			<input {...inputProps} type="checkbox" bind:checked {value} bind:this={input} />
		{/if}
		<div class="absolute inset-0 flex justify-center items-center [:not(:checked)+&]:hidden pointer-events-none">
			{#if rest.type === "checkbox"}
				<Icon icon={check} class="size-4 text-white" />
			{:else}
				<div class="size-2 bg-white rounded-full"></div>
			{/if}
		</div>
		{#if showValidationPopup && validityMessage}
			<div class="absolute bottom-full left-0 z-10 mb-2.5 w-max max-w-64">
				<div
					class="relative rounded-2xl border-2 border-fcrerror bg-white px-4 py-2.5 text-sm text-fcrdarkgray shadow-lg"
				>
					<Formatted text={validityMessage} />
					<div
						class="absolute left-2 top-full size-0 border-x-8 border-x-transparent border-t-8 border-t-fcrerror"
					></div>
					<div
						class="absolute left-2 top-full -mt-0.5 size-0 border-x-[7px] border-x-transparent border-t-[7px] border-t-white"
					></div>
				</div>
			</div>
		{/if}
	{:else}
		<div class="relative">
			<input {...inputProps} bind:value bind:this={input} />
			{#if placeholder}
				<div
					class={[
						"absolute inset-0 text-fcrgray flex px-6 items-center font-medium duration-200",
						"transition-all pointer-events-none size-full",
						"peer-focus:text-xs/6 peer-focus:-translate-y-2",
						value ? "text-xs/6 -translate-y-2" : "translate-x-px",
					]}
					id={componentId}
				>
					<span class="h-6 line-clamp-1">
						<Formatted text={placeholder} />
					</span>
				</div>
			{/if}
			{#if validationStyle === InputValidationStyle.Loading}
				<div class="absolute inset-0 flex items-center justify-end pointer-events-none">
					<div class="pr-6">
						<Icon
							icon={loaderIcon}
							class="size-5.5 text-fcrgray animate-[spin_1s_steps(8,_jump-end)_infinite_reverse]"
						/>
					</div>
				</div>
			{:else if validationStyle === InputValidationStyle.Warning && displayMessage}
				<div class="absolute inset-0 flex items-center justify-end pointer-events-none">
					<div class="pr-6">
						<Icon icon={errorIcon} class="size-5.5 text-fcrorange" />
					</div>
				</div>
			{:else if validationStyle === InputValidationStyle.Success && displayMessage}
				<div class="absolute inset-0 flex items-center justify-end pointer-events-none">
					<div class="pr-6">
						<Icon icon={circleCheckFillIcon} class="size-5.5 text-fcrsuccess" />
					</div>
				</div>
			{:else}
				<div
					class={[
						"absolute inset-0 hidden items-center justify-end pointer-events-none",
						displayMessage && "peer-[:user-invalid]:flex",
					]}
				>
					<div class="pr-6">
						<Icon icon={errorIcon} class="size-5.5 text-fcrerror" />
					</div>
				</div>
			{/if}
		</div>
		{#if validationStyle === InputValidationStyle.Warning && displayMessage}
			<div class="justify-end text-sm text-fcrorange pr-6 pt-1 flex">
				<span><Formatted text={displayMessage} /></span>
			</div>
		{:else if validationStyle === InputValidationStyle.Success && displayMessage}
			<div class="justify-end text-sm text-fcrsuccess pr-6 pt-1 flex">
				<span><Formatted text={displayMessage} /></span>
			</div>
		{:else if validationStyle !== InputValidationStyle.Loading}
			<div class="justify-end text-sm text-fcrerror pr-6 pt-1 hidden [:has(:user-invalid)+&:not(:empty)]:flex">
				<span><Formatted text={displayMessage} /></span>
			</div>
		{/if}
	{/if}
</div>
